import { deriveJobTitle, type JobRow } from "@loumy/contracts";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../auth/session";
import { JOB_DRAG_TYPE } from "../lib/folderTree";
import { duration, relativeTime, titleCase } from "../lib/format";
import { getCore } from "../registry/toolRegistry";
import { useJobProgressStore } from "../state/jobProgressStore";
import { useFolders, useJobPreviews, useJobs, useMoveJobOutputs } from "../state/queries";
import { useUiStore } from "../state/uiStore";
import { Icon, MediaThumb, ProgressBar, Spinner, cx, type IconName } from "../ui";

// §11 Jobs-Panel. Letzte Jobs des Users (interne API). Live-Updates kommen ausschließlich
// über den DO-Push (D47, WS) — realtime-only, kein Polling; bei Reconnect holt der Delta-Sync
// verpasste Übergänge nach. D155: der Job-Status steht LINKS als zustandsabhängiges Icon (statt
// als Status-Badge rechts) — ein FERTIGER Job zeigt dort sein Vorschau-Bild, exakt wie die
// FolderTree-Asset-Zeilen (gemeinsame `MediaThumb`, D142). Die Row ist damit 1:1 eine FolderTree-
// Zeile (nur Thumbnail + Titel; Credits-Chip entfernt). D65: ein Klick öffnet den Job im
// zugehörigen Tool (Settings/Inputs/Output rehydriert) — Kategorie/Tool über die Registry, kein
// hartes Tool-Ref (§4). D100: ein FERTIGER Job ist draggable — Drop auf einen Ordner im FolderTree
// direkt darüber verschiebt ALLE seine Ergebnis-Assets (POST /v1/jobs/:id/move-outputs). D161:
// dieselbe Verschiebung gibt es als per-Job-Button „In <gewählter Ordner> verschieben" (erscheint
// nur, wenn ein echter Ordner gewählt ist und der Job FERTIG ist) — Outputs landen beim Submit
// IMMER im tool-Default (autoFolder, D161), der gewählte Ordner ist nur noch das Verschiebe-Ziel.

function statusLabel(job: JobRow): string {
  return job.phase ? `${job.status}·${job.phase}` : job.status;
}

/**
 * D155: links statt eines Status-TAGs ein zustandsabhängiges Icon — und ein FERTIGER Job zeigt
 * sein Vorschau-Bild (genau wie die FolderTree-Asset-Zeilen, D142). queued = Uhr, processing =
 * Spinner, done = Bild (Fallback ohne presigntes Bild: generisches „image"-Icon), failed = ✕
 * (rot), canceled = −.
 */
function jobThumb(
  job: JobRow,
  previewUrl: string | null,
): { url?: string | null; icon: IconName; spinner: boolean; danger: boolean } {
  switch (job.status) {
    case "processing":
      return { icon: "clock", spinner: true, danger: false };
    case "done":
      return { url: previewUrl, icon: "image", spinner: false, danger: false };
    case "failed":
      return { icon: "x", spinner: false, danger: true };
    case "canceled":
      return { icon: "minus", spinner: false, danger: false };
    case "queued":
    default:
      return { icon: "clock", spinner: false, danger: false };
  }
}

/**
 * D98/D142: Job-Titel für die Liste — die Ableitung lebt zentral in `deriveJobTitle`
 * (@loumy/contracts, SSOT): enhance-Signal → gekürzter settings.prompt → undefined
 * (→ Tool-Name). Dieselbe Quelle nutzt der Server für die Ordner-Inhalte (D142).
 */
export function jobTitle(job: JobRow): string | undefined {
  return deriveJobTitle(job.executed_pipeline_spec, job.settings);
}

const toMs = (v: Date | string): number => (v instanceof Date ? v.getTime() : new Date(v).getTime());

/**
 * D164: benötigte (Wand-)Zeit eines Jobs in ms — LIVE während `processing` (jetzt − Start), sonst
 * die fertige Spanne (`finished_at − started_at`). Quelle ist primär `started_at`; fehlt es (alter
 * Job/Rennen), fällt nur der LAUFENDE Fall auf `created_at` zurück. Ist keine belastbare Spanne
 * ableitbar (queued, oder terminal ohne Zeitstempel) → null ⇒ die Zeitspalte bleibt leer.
 */
export function jobDurationMs(job: JobRow, now: number): number | null {
  const startTs = job.started_at ?? null;
  if (job.status === "processing") {
    // started_at fehlt nur im kurzen Übergangsfenster, bis der Server es beim Eintritt in
    // 'processing' setzt; der created_at-Fallback rechnet dann die (kurze) Queue-Zeit mit ein.
    const start = toMs(startTs ?? job.created_at);
    return Number.isFinite(start) ? Math.max(0, now - start) : null;
  }
  if (job.status === "done" || job.status === "failed" || job.status === "canceled") {
    if (!startTs || !job.finished_at) return null;
    const d = toMs(job.finished_at) - toMs(startTs);
    return Number.isFinite(d) && d >= 0 ? d : null;
  }
  return null; // queued: noch nicht gestartet, keine Dauer
}

/**
 * D164: tickt im Sekundentakt, solange `active` (= diese Row zeigt einen laufenden Job). Inaktiv
 * läuft KEIN Intervall — terminale Rows haben eine statische Dauer und brauchen kein Re-Render.
 * Pro Row ein eigener Tick; da typischerweise nur 0–1 Job zugleich `processing` ist, bleibt es bei
 * höchstens wenigen Intervallen.
 */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now()); // beim Aktivieren sofort frisch (kein bis zu 1 s alter Initialwert)
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

function JobItem({
  job,
  previewUrl,
  moveTarget,
}: {
  job: JobRow;
  previewUrl: string | null;
  // D161: Ziel des „verschieben"-Buttons — Name des AKTUELL gewählten Ordners (null = kein
  // echter Ordner gewählt ⇒ kein Button). `onMove` zieht ALLE Ergebnis-Assets dorthin;
  // `pendingJobId` sperrt den Button des gerade laufenden Moves (Doppel-Klick-Schutz).
  moveTarget: { folderName: string; pendingJobId: string | null; onMove: (jobId: string) => void } | null;
}) {
  const navigate = useNavigate();
  const openJob = useUiStore((s) => s.openJob);
  const active = useUiStore((s) => s.openedJobId === job.id);
  // D147: per-Job-Selektor → nur dieses Item re-rendert, wenn sein eigener Progress-Eintrag wechselt.
  const progress = useJobProgressStore((s) => s.byJob[job.id]);
  const core = getCore(job.tool_id);
  const created = job.created_at instanceof Date ? job.created_at : new Date(job.created_at);
  const title = jobTitle(job); // D98
  // D164: benötigte Zeit rechts in der Row — bei `processing` tickt `useNow` im Sekundentakt (live),
  // sonst (terminal) ist die Spanne statisch und es läuft kein Intervall. null ⇒ Spalte leer. `now`
  // wird NUR im processing-Zweig von jobDurationMs gelesen — terminal zählt finished_at − started_at
  // (das eingefrorene `now` bei running=false ist dort bedeutungslos, daher kein Stale-Effekt).
  const running = job.status === "processing";
  const now = useNow(running);
  const elapsedMs = jobDurationMs(job, now);
  const durationLabel = elapsedMs == null ? null : duration(elapsedMs);
  // D155: links das zustandsabhängige Icon bzw. (fertig) das Vorschau-Bild (Eingabe-Bild Vorrang,
  // Output-Fallback; serverseitig presignt). Ohne Bild fällt ein fertiger Job auf das „image"-Icon.
  const thumb = jobThumb(job, previewUrl);

  // Job öffnen: im Store markieren + zum zugehörigen Tool navigieren (Studio rehydriert). Ohne
  // registrierten Core (deprecated Tool, D32) bleibt der Job nicht öffenbar → Klick deaktiviert.
  const open = () => {
    if (!core) return;
    openJob(job.id);
    navigate(`/generate/${core.category}/${core.id}`);
  };

  // D100: nur fertige Jobs sind ziehbar (vorher existieren keine Ergebnis-Assets).
  const draggable = job.status === "done";
  // D161: der „verschieben"-Button erscheint nur bei einem FERTIGEN Job (sonst keine Outputs) UND
  // wenn ein echter Ordner gewählt ist (moveTarget != null).
  const canMove = draggable && moveTarget !== null;

  return (
    <li
      draggable={draggable || undefined}
      onDragStart={
        draggable
          ? (e) => {
              e.dataTransfer.setData(JOB_DRAG_TYPE, job.id);
              e.dataTransfer.effectAllowed = "move";
            }
          : undefined
      }
      className={cx("group flex items-center gap-1", draggable && "cursor-grab select-none")}
    >
      <button
        type="button"
        onClick={open}
        disabled={!core}
        aria-current={active ? "true" : undefined}
        // Status/Zeit wandern in den Tooltip (die Row zeigt 1:1 wie eine FolderTree-Zeile nur Bild + Titel).
        title={
          core
            ? `${titleCase(job.tool_id)} · ${statusLabel(job)} · ${relativeTime(created)}${draggable ? " — ziehen, um die Ergebnisse in einen Ordner zu verschieben" : ""}`
            : "Tool nicht mehr verfügbar"
        }
        className={cx(
          "flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
          core ? "cursor-pointer hover:bg-surface-2" : "cursor-not-allowed opacity-60",
          active && "bg-surface-2 ring-1 ring-accent/40",
        )}
      >
        {/* D155: LINKS das Zustands-Icon bzw. (fertig) das Bild — gemeinsame MediaThumb wie der FolderTree. */}
        <MediaThumb url={thumb.url} icon={thumb.icon} spinner={thumb.spinner} danger={thumb.danger} />
        <div className="min-w-0 flex-1">
          {/* D98: Auto-Titel (enhance-Signal, Fallback gekürzter Prompt) — sonst Tool-Name. */}
          <span className="block truncate text-[12px] text-text">{title ?? titleCase(job.tool_id)}</span>
          {/* D147: Live-Step-Progressbar — nur während der Job läuft UND ein Push eingetroffen ist.
              D169: gemeinsame `ProgressBar` (determinate i/n) statt eigener Markup-Kopie. */}
          {job.status === "processing" && progress && (
            <ProgressBar
              value={Math.min(progress.index, progress.count)}
              max={progress.count}
              label={`Schritt ${Math.min(progress.index, progress.count)} von ${progress.count}`}
              className="mt-1.5"
            />
          )}
        </div>
      </button>
      {/* D164: benötigte Zeit rechts — live tickend während `processing` (betont), sonst die fertige
          Dauer (muted). Leer (kein Span), solange keine belastbare Spanne existiert (queued). */}
      {durationLabel && (
        <span
          className={cx("shrink-0 pr-1 text-[11px] tabular-nums", running ? "text-text" : "text-muted")}
          title={running ? "Laufzeit (läuft)" : "Benötigte Zeit"}
        >
          {durationLabel}
        </span>
      )}
      {/* D161: per-Job-Verschieben in den AKTUELL gewählten Ordner (POST /v1/jobs/:id/move-outputs,
          dieselbe Mutation wie der D100-Drop). Erscheint auf Row-Hover/Fokus; der Ordnername steht
          im Tooltip (kompakt, Editor-UI-Präferenz — kein Dauer-Text je Zeile). */}
      {canMove && moveTarget && (
        <button
          type="button"
          onClick={() => moveTarget.onMove(job.id)}
          disabled={moveTarget.pendingJobId === job.id}
          title={`Ergebnisse in „${moveTarget.folderName}" verschieben`}
          aria-label={`Ergebnisse in Ordner „${moveTarget.folderName}" verschieben`}
          className="shrink-0 rounded-md p-1 text-muted opacity-0 transition-opacity hover:bg-surface-2 hover:text-text focus-visible:opacity-100 group-hover:opacity-100 disabled:cursor-wait disabled:opacity-100"
        >
          <Icon name={moveTarget.pendingJobId === job.id ? "clock" : "folder"} size={15} />
        </button>
      )}
    </li>
  );
}

export function JobsList() {
  const { status } = useSession();
  const jobs = useJobs();
  // D155: presignte Vorschau-Bilder je Job — EINMAL hier abonnieren und je Item durchreichen
  // (statt N Subscriptions). Fehlt der Eintrag (noch nicht geladen / alter Worker) → null = Icon.
  const previews = useJobPreviews();
  // D161: das Ziel des per-Job-„verschieben"-Buttons = der AKTUELL gewählte Ordner. Name aus der
  // Folder-Liste (für Button-Tooltip); null = Inbox/kein echter Ordner ⇒ kein Button. Die Mutation
  // (POST /v1/jobs/:id/move-outputs) ist dieselbe wie der D100-FolderTree-Drop.
  const folders = useFolders();
  const selectedFolderId = useUiStore((s) => s.selectedFolderId);
  const moveJobOutputs = useMoveJobOutputs();
  // D161: eine kurze Statuszeile unter der Liste, falls ein Move nichts bewirkt (moved=0 = Job ohne
  // (mehr vorhandene) Outputs, serverseitig legitim) oder fehlschlägt — wie der D100-Drop-Pfad.
  const [moveNotice, setMoveNotice] = useState<string | null>(null);
  const selectedFolderName =
    selectedFolderId == null ? null : (folders.data?.find((f) => f.id === selectedFolderId)?.name ?? null);
  const moveTarget =
    selectedFolderId != null && selectedFolderName != null
      ? {
          folderName: selectedFolderName,
          pendingJobId: moveJobOutputs.isPending ? (moveJobOutputs.variables?.id ?? null) : null,
          onMove: (jobId: string) => {
            setMoveNotice(null);
            moveJobOutputs.mutate(
              { id: jobId, folderId: selectedFolderId },
              {
                onSuccess: (res) =>
                  setMoveNotice(res.moved === 0 ? "Dieser Job hat keine (mehr vorhandenen) Ergebnis-Assets." : null),
                onError: (e) => setMoveNotice(e instanceof Error ? e.message : "Verschieben fehlgeschlagen."),
              },
            );
          },
        }
      : null;

  if (status !== "signed-in") return <p className="px-2 py-3 text-[11px] text-muted">Anmelden, um Jobs zu sehen.</p>;
  if (jobs.isLoading)
    return (
      <div className="px-2 py-3">
        <Spinner size={16} />
      </div>
    );
  if (!jobs.data || jobs.data.length === 0)
    return <p className="px-2 py-3 text-[11px] text-muted">Noch keine Jobs.</p>;

  return (
    <>
      <ul className="space-y-0.5">
        {jobs.data.map((j) => (
          <JobItem key={j.id} job={j} previewUrl={previews.data?.[j.id] ?? null} moveTarget={moveTarget} />
        ))}
      </ul>
      {moveNotice && <p className="px-2 py-1.5 text-[11px] text-muted">{moveNotice}</p>}
    </>
  );
}
